import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

type TvModeContextType = {
  tvMode: boolean;
  toggleTvMode: () => void;
  setTvMode: (value: boolean) => void;
};

const TvModeContext = createContext<TvModeContextType | undefined>(undefined);

export const TvModeProvider = ({ children }: { children: ReactNode }) => {
  const [tvMode, setTvModeState] = useState(false);
  const toggleTvMode = () => {
    setTvModeState((prev) => !prev);
  };
  const setTvMode = (value: boolean) => {
    setTvModeState(value);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const doc = document as Document & {
        webkitFullscreenElement?: Element;
        msFullscreenElement?: Element;
      };
      const isFullscreen = !!(document.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement);

      if (!isFullscreen && tvMode) {
        setTvModeState(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
    };
  }, [tvMode]);

  useEffect(() => {
    if (tvMode) {
      // Enter fullscreen
      const el = document.documentElement as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void>;
        msRequestFullscreen?: () => Promise<void>;
      };
      if (el.requestFullscreen) {
        el.requestFullscreen().catch(() => {});
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      const doc = document as Document & {
        webkitExitFullscreen?: () => Promise<void>;
        msExitFullscreen?: () => Promise<void>;
        webkitFullscreenElement?: Element;
        msFullscreenElement?: Element;
      };
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      } else if (doc.webkitFullscreenElement) {
        doc.webkitExitFullscreen?.();
      } else if (doc.msFullscreenElement) {
        doc.msExitFullscreen?.();
      }
    }
  }, [tvMode]);

  return <TvModeContext.Provider value={{ tvMode, toggleTvMode, setTvMode }}>{children}</TvModeContext.Provider>;
};

export const useTvMode = () => {
  const context = useContext(TvModeContext);
  if (!context) throw new Error("useTvMode must be used within TvModeProvider");
  return context;
};
